// pages/UserManagement/Species.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
  Fade,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Category as CategoryIcon,
  Edit as EditIcon,
  ColorLens as ColorLensIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Shield as ShieldIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { getCurrentUser, getAuthToken } from "../../utils/AuthHelper";
import { backend_url } from "../../App";

export default function Species() {
  const user = getCurrentUser();
  const token = getAuthToken();
  const isMounted = useRef(true);
  const initialLoadDone = useRef(false);

  const [species, setSpecies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [showEnabledOnly, setShowEnabledOnly] = useState(true);

  // Dialogs
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // Forms
  const [speciesForm, setSpeciesForm] = useState({
    id: "",
    name: "",
    endangered: false,
    color: "#4B96C0",
    enabled: true,
    protected: true,
  });
  const [editingSpecies, setEditingSpecies] = useState(null);

  // Set up isMounted ref
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetchWithAuth = useCallback(
    async (url, options = {}) => {
      const headers = {
        Authorization: `Token ${token}`,
        "Content-Type": "application/json",
        ...options.headers,
      };

      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (!response.ok) {
        try {
          const errorData = await response.json();
          throw new Error(
            errorData.detail ||
              errorData.message ||
              `Request failed with status ${response.status}`,
          );
        } catch (e) {
          if (e instanceof SyntaxError) {
            throw new Error(
              response.statusText ||
                `Request failed with status ${response.status}`,
            );
          }
          throw e;
        }
      }

      const contentType = response.headers.get("content-type");
      if (
        contentType &&
        contentType.includes("application/json") &&
        response.status !== 204
      ) {
        try {
          return await response.json();
        } catch (e) {
          console.log("Response is not JSON or empty");
          return null;
        }
      }

      return null;
    },
    [token],
  );

  const fetchSpecies = useCallback(async () => {
    if (!isMounted.current) return;

    setLoading(true);
    try {
      let speciesList = [];

      if (user?.user_type === "admin") {
        // System admin sees all species
        const data = await fetchWithAuth(`${backend_url}/core/api/specie/`);
        speciesList = (data?.results || data || []).map((s) => ({
          ...s,
          id: s.id,
        }));
      } else if (user?.user_type === "superuser" && user?.organization?.id) {
        // Organization admin sees species assigned to their organization
        const orgPermData = await fetchWithAuth(
          `${backend_url}/core/api/organization-species-permissions/?organization=${user.organization.id}`,
        );

        const orgPermissions = orgPermData?.results || orgPermData || [];

        // Extract species IDs from permissions
        const speciesIds = orgPermissions.map((p) =>
          typeof p.specie === "object" ? p.specie.id : p.specie,
        );

        if (speciesIds.length > 0) {
          // Fetch full species details for each ID
          const speciesPromises = speciesIds.map(async (specieId) => {
            try {
              const specieData = await fetchWithAuth(
                `${backend_url}/core/api/specie/${specieId}/`,
              );
              return specieData;
            } catch (err) {
              console.error(`Error fetching species ${specieId}:`, err);
              return null;
            }
          });

          speciesList = (await Promise.all(speciesPromises)).filter(
            (s) => s !== null,
          );
        }
      } else if (user?.user_type === "user" || user?.user_type === "viewer") {
        // Regular user sees species assigned to them personally
        const userPermData = await fetchWithAuth(
          `${backend_url}/core/api/user-species-permissions/?user=${user.id}`,
        );

        const userPermissions = userPermData?.results || userPermData || [];

        // Extract species IDs from permissions
        const speciesIds = userPermissions.map((p) =>
          typeof p.specie === "object" ? p.specie.id : p.specie,
        );

        if (speciesIds.length > 0) {
          // Fetch full species details for each ID
          const speciesPromises = speciesIds.map(async (specieId) => {
            try {
              const specieData = await fetchWithAuth(
                `${backend_url}/core/api/specie/${specieId}/`,
              );
              return specieData;
            } catch (err) {
              console.error(`Error fetching species ${specieId}:`, err);
              return null;
            }
          });

          speciesList = (await Promise.all(speciesPromises)).filter(
            (s) => s !== null,
          );
        }
      }

      if (isMounted.current) {
        setSpecies(speciesList);
        setLoading(false);
        initialLoadDone.current = true;
      }
    } catch (err) {
      console.error("Error fetching species:", err);
      if (isMounted.current) {
        setError("Failed to load data. Please try again.");
        setLoading(false);
      }
    }
  }, [user, fetchWithAuth]);

  // Initial load - only once
  useEffect(() => {
    if (!initialLoadDone.current) {
      fetchSpecies();
    }

    return () => {
      // Cleanup if needed
    };
  }, [fetchSpecies]); // Only depends on fetchSpecies

  const filteredSpecies = species.filter((specie) => {
    const matchesSearch =
      specie.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      specie.id?.toString().toLowerCase().includes(searchTerm.toLowerCase());

    const matchesEnabled = !showEnabledOnly || specie.enabled;

    return matchesSearch && matchesEnabled;
  });

  const handleOpenAddDialog = () => {
    setSpeciesForm({
      id: "",
      name: "",
      endangered: false,
      color: "#4B96C0",
      enabled: true,
      protected: true,
    });
    setAddDialogOpen(true);
  };

  const handleOpenEditDialog = (specie) => {
    setEditingSpecies(specie);
    setSpeciesForm({
      id: specie.id,
      name: specie.name,
      endangered: specie.endangered,
      color: specie.color,
      enabled: specie.enabled,
      protected: specie.protected,
    });
    setEditDialogOpen(true);
  };

  const handleSaveSpecies = async (isEdit = false) => {
    try {
      const url = isEdit
        ? `${backend_url}/core/api/specie/${editingSpecies.id}/`
        : `${backend_url}/core/api/specie/`;

      const method = isEdit ? "PATCH" : "POST";

      await fetchWithAuth(url, {
        method,
        body: JSON.stringify(speciesForm),
      });

      setSuccess(`Species ${isEdit ? "updated" : "created"} successfully`);

      // Refresh the list
      await fetchSpecies();

      setAddDialogOpen(false);
      setEditDialogOpen(false);
      setEditingSpecies(null);
    } catch (err) {
      console.error("Error saving species:", err);
      setError(err.message || "Failed to save species");
    }
  };

  const handleToggleSpeciesStatus = async (specie) => {
    try {
      await fetchWithAuth(`${backend_url}/core/api/specie/${specie.id}/`, {
        method: "PATCH",
        body: JSON.stringify({ enabled: !specie.enabled }),
      });

      setSuccess(
        `Species ${specie.enabled ? "disabled" : "enabled"} successfully`,
      );

      // Refresh the list
      await fetchSpecies();
    } catch (err) {
      console.error("Error toggling species status:", err);
      setError("Failed to update species status");
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    try {
      await fetchSpecies();
    } catch (err) {
      console.error("Error refreshing data:", err);
      setError("Failed to refresh data");
    }
  };

  // Get user-friendly role name
  const getUserRoleName = () => {
    switch (user?.user_type) {
      case "admin":
        return "System Administrator";
      case "superuser":
        return "Organization Administrator";
      case "user":
        return "Regular User";
      case "viewer":
        return "Viewer";
      default:
        return "User";
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box>
            <Typography
              variant="h5"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 2 }}
            >
              <CategoryIcon />
              Species Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.user_type === "admin"
                ? "Manage all wildlife species in the system"
                : user?.user_type === "superuser"
                  ? `View species available to your organization (${user?.organization?.name})`
                  : "View species you have access to"}
            </Typography>
            <Typography
              variant="caption"
              color="primary"
              sx={{ mt: 1, display: "block" }}
            >
              Role: {getUserRoleName()}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={loading}
            >
              Refresh
            </Button>

            {user?.user_type === "admin" && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenAddDialog}
              >
                Add Species
              </Button>
            )}
          </Box>
        </Box>

        {/* Filters */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search species by name or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={showEnabledOnly}
                  onChange={(e) => setShowEnabledOnly(e.target.checked)}
                />
              }
              label="Show Enabled Only"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Species Grid */}
      <Fade in={!loading} timeout={300}>
        <div>
          <Grid container spacing={3}>
            {loading ? (
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                  <CircularProgress />
                </Box>
              </Grid>
            ) : filteredSpecies.length > 0 ? (
              filteredSpecies.map((specie) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={specie.id}>
                  <Card
                    sx={{
                      borderRadius: 3,
                      height: "100%",
                      border: `1px solid ${specie.color}40`,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: `0 8px 32px ${specie.color}40`,
                      },
                    }}
                  >
                    <CardContent>
                      {/* Species Header */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            backgroundColor: specie.color,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                            fontWeight: 600,
                            fontSize: "1.2rem",
                          }}
                        >
                          {specie.name?.[0] || "?"}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" fontWeight={600} noWrap>
                            {specie.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {specie.id}
                          </Typography>
                        </Box>
                        <Box>
                          <Chip
                            label={specie.enabled ? "Enabled" : "Disabled"}
                            size="small"
                            color={specie.enabled ? "success" : "default"}
                            variant="outlined"
                          />
                        </Box>
                      </Box>

                      {/* Species Details */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                          mb: 3,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Status
                          </Typography>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            {specie.endangered && (
                              <Chip
                                icon={<WarningIcon />}
                                label="Endangered"
                                color="error"
                                size="small"
                              />
                            )}
                            {specie.protected && (
                              <Chip
                                icon={<ShieldIcon />}
                                label="Protected"
                                color="warning"
                                size="small"
                              />
                            )}
                          </Box>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Color
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                backgroundColor: specie.color,
                                border: "1px solid #ccc",
                              }}
                            />
                            <Typography variant="caption">
                              {specie.color}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      {/* Actions - Only show edit/disable for system admin */}
                      {user?.user_type === "admin" && (
                        <>
                          <Divider sx={{ my: 2 }} />
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <Tooltip
                              title={specie.enabled ? "Disable" : "Enable"}
                            >
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleToggleSpeciesStatus(specie)
                                }
                                color={specie.enabled ? "default" : "primary"}
                              >
                                {specie.enabled ? (
                                  <VisibilityOffIcon />
                                ) : (
                                  <VisibilityIcon />
                                )}
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEditDialog(specie)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Paper sx={{ p: 4, textAlign: "center" }}>
                  <Typography color="text.secondary">
                    {user?.user_type === "admin"
                      ? "No species found. Click 'Add Species' to create one."
                      : user?.user_type === "superuser"
                        ? "No species have been assigned to your organization yet. Contact your system administrator."
                        : "No species have been assigned to you yet. Contact your organization administrator."}
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </div>
      </Fade>

      {/* Add Species Dialog - Only for system admin */}
      {user?.user_type === "admin" && (
        <Dialog
          open={addDialogOpen}
          onClose={() => setAddDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Add New Species</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Species ID"
                  value={speciesForm.id}
                  onChange={(e) =>
                    setSpeciesForm({
                      ...speciesForm,
                      id: e.target.value.toLowerCase().replace(/ /g, "_"),
                    })
                  }
                  helperText="Lowercase with underscores (e.g., 'snow_leopard')"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Species Name"
                  value={speciesForm.name}
                  onChange={(e) =>
                    setSpeciesForm({ ...speciesForm, name: e.target.value })
                  }
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Color"
                  type="color"
                  value={speciesForm.color}
                  onChange={(e) =>
                    setSpeciesForm({ ...speciesForm, color: e.target.value })
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ColorLensIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={speciesForm.endangered}
                      onChange={(e) =>
                        setSpeciesForm({
                          ...speciesForm,
                          endangered: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Endangered Species"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={speciesForm.protected}
                      onChange={(e) =>
                        setSpeciesForm({
                          ...speciesForm,
                          protected: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Protected"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={() => handleSaveSpecies(false)}
              variant="contained"
            >
              Create Species
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Edit Species Dialog - Only for system admin */}
      {user?.user_type === "admin" && (
        <Dialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Edit Species</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Species ID"
                  value={speciesForm.id}
                  disabled
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Species Name"
                  value={speciesForm.name}
                  onChange={(e) =>
                    setSpeciesForm({ ...speciesForm, name: e.target.value })
                  }
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Color"
                  type="color"
                  value={speciesForm.color}
                  onChange={(e) =>
                    setSpeciesForm({ ...speciesForm, color: e.target.value })
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ColorLensIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={speciesForm.endangered}
                      onChange={(e) =>
                        setSpeciesForm({
                          ...speciesForm,
                          endangered: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Endangered Species"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={speciesForm.protected}
                      onChange={(e) =>
                        setSpeciesForm({
                          ...speciesForm,
                          protected: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Protected"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={speciesForm.enabled}
                      onChange={(e) =>
                        setSpeciesForm({
                          ...speciesForm,
                          enabled: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Enabled"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={() => handleSaveSpecies(true)} variant="contained">
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Snackbars */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
}
